import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives/responsive";

import { Adg4Button } from "@/components/adg4/button/src/button";
import { wacToken } from "@/libraries/design/foundations";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";

const sectionContainer = css({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column"
});

const contentContainer = css({
    display: "flex",
    flexDirection: "row",
    maxWidth: "1168px",
    marginBlock: "2rem",
    gap: 108,
    [media.only.md]: {
        gap: 48
    },
    [media.only.sm]: {
        gap: 36
    },
    [media.below.sm]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 24
    }
});

const textContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    [media.below.sm]: {
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center"
    }
});

const headingTextStyles = css({
    marginBlock: "0px"
});
const bodyTextStyles = css({
    marginBlock: "0px",
    marginBlockStart: ".5rem",
    marginBlockEnd: "1.5rem",
    maxWidth: "351px",

    [media.below.md]: {
        maxWidth: "306px"
    }
});

const iconContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    flexBasis: "100%",
    gap: "24px 32px",
    justifyContent: "center",
    alignContent: "center",
    maxWidth: 554,
    flexShrink: 0,
    listStyle: "none",
    paddingInline: 0,

    [media.only.md]: {
        gap: "20px 24px",
        maxWidth: 458
    },
    [media.below.md]: {
        gap: 16,
        maxWidth: 306
    }
});

const iconStyles = css({
    borderRadius: "27px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animationIterationCount: 1,
    animationDuration: "0.75s",
    background: wacToken("color.neutral.light0"),
    width: "114px",
    height: "114px",
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        maxWidth: "50%",
        maxHeight: "50%",
        width: "auto",
        height: "auto"
    },

    [media.only.md]: {
        borderRadius: "15px",
        width: "96px",
        height: "96px"
    },
    [media.below.md]: {
        borderRadius: "15px",
        width: "64px",
        height: "64px"
    }
});

const linkStyles = css({
    "cursor": "pointer",
    "boxShadow": "0px 1px 1px 0px #091E4240,  0px 0px 1px 0px #091E424F",

    "&:hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F"
    }
});

const animationStyle = css({
    "@keyframes hopup": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "0%": {
            transform: "translateY(0)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "50%": {
            transform: "translateY(-15%)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "100%": {
            transform: "translateY(0)"
        }
    },
    "animationName": "hopup"
});

export type IconDataProps = {
    url: string;
    altText: string;
    href?: string;
};

export type Adg4OffsetIconCollectionProps = {
    iconData: IconDataProps[];
    /**
     * The position of the text section relative to the icon collection.
     * @default "right"
     */
    textPosition?: "left" | "right";
    headingText: string;
    bodyText: string;
    ctaText: string;
    ctaLink: string;
    productKey?: ProductKeyTypes;
    ctaOnClickFunc?: () => void;
};

export const Adg4OffsetIconCollection = ({
    iconData,
    textPosition = "right",
    headingText,
    bodyText,
    ctaText,
    ctaLink,
    productKey,
    ctaOnClickFunc
}: Adg4OffsetIconCollectionProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    const isExternalHref = ctaLink.startsWith("https://");

    const textSection = (
        <div css={textContainerStyles}>
            <h3 css={headingTextStyles}>{headingText}</h3>
            <p css={bodyTextStyles}>{bodyText}</p>
            <Adg4Button
                variant="secondary"
                size="large"
                color="black"
                href={ctaLink}
                target={isExternalHref ? "_blank" : undefined}
                rel={isExternalHref ? "noreferrer noopener" : undefined}
                onClick={ctaOnClickFunc}
                dataEventProps={
                    productKey
                        ? {
                              "data-product-key": productKey,
                              "data-event": "clicked",
                              "data-event-component": "linkButton",
                              "data-event-container": "integrationsSection",
                              "data-label": "integrations-cta",
                              "data-track-event": "integrations.cta"
                          }
                        : undefined
                }
            >
                {ctaText}
            </Adg4Button>
        </div>
    );

    return (
        <div css={sectionContainer}>
            <div css={contentContainer}>
                {textPosition !== "right" && textSection}
                <ul css={iconContainerStyles} ref={scrollRef}>
                    {iconData.map((icon, index) => (
                        <li key={index}>
                            {icon.href ? (
                                <a
                                    href={icon.href}
                                    css={[
                                        iconStyles,
                                        linkStyles,
                                        sectionIsVisible ? animationStyle : null
                                    ]}
                                    style={{
                                        animationDelay: `${0.5 + 0.25 * index}s`
                                    }}
                                    data-product-key={productKey}
                                    data-event-container="integrationsSection"
                                    data-event-component="linkButton"
                                    data-label={icon.altText}
                                    data-event="clicked"
                                    data-track-event="integrations.icon"
                                >
                                    {/* eslint-disable-next-line no-restricted-syntax */}
                                    <img src={icon.url} alt={icon.altText} />
                                </a>
                            ) : (
                                <div
                                    css={[
                                        iconStyles,
                                        sectionIsVisible ? animationStyle : null
                                    ]}
                                    style={{
                                        animationDelay: `${0.5 + 0.25 * index}s`
                                    }}
                                >
                                    {/* eslint-disable-next-line no-restricted-syntax */}
                                    <img src={icon.url} alt={icon.altText} />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                {textPosition === "right" && textSection}
            </div>
        </div>
    );
};
